import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useExchangeRate } from '@/hooks/useExchangeRate';

type DisplayCurrency = 'EUR' | 'USD';

interface CurrencyContextType {
  displayCurrency: DisplayCurrency;
  setDisplayCurrency: (currency: DisplayCurrency) => void;
  convert: (amount: number, fromCurrency: string) => number;
  rates: { EUR: number; USD: number };
  ratesLoading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType>({
  displayCurrency: 'EUR',
  setDisplayCurrency: () => {},
  convert: (amount) => amount,
  rates: { EUR: 1, USD: 1.08 },
  ratesLoading: true,
});

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [displayCurrency, setDisplayCurrency] = useState<DisplayCurrency>(() => {
    const saved = localStorage.getItem('displayCurrency');
    return (saved === 'EUR' || saved === 'USD') ? saved : 'EUR';
  });

  const { rates, loading: ratesLoading, convert: exchangeConvert } = useExchangeRate();

  useEffect(() => {
    localStorage.setItem('displayCurrency', displayCurrency);
  }, [displayCurrency]);

  // Convert amount from source currency to display currency
  const convert = (amount: number, fromCurrency: string): number => {
    return exchangeConvert(amount, fromCurrency, displayCurrency);
  };

  return (
    <CurrencyContext.Provider value={{ 
      displayCurrency, 
      setDisplayCurrency, 
      convert, 
      rates, 
      ratesLoading 
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
